import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import CustomBreadCrumbs from "../../../common/breadcrumbs";
import {
  sectionDetailsRequest,
  sectionUpdateDetailRequest,
  fieldCreateRequest,
  fieldUpdateOrderRequest,
  fieldUpdateRequest,
  deleteFieldRequest,
} from "../../../../store/slices/customFieldSlice";
import { AccessScopeConstants } from "../../../../constants";
import * as RoleHelper from "../../../../helpers/RoleHelper";
import { Button } from "primereact/button";
import { TabMenu } from "primereact/tabmenu";
import Form from "../../../common/form";
import AddCustomFieldSectionForm from "../add/AddCustomFieldSectionForm";
import List from "../../../common/list";
import { GlobalConstants } from "../../../../constants";
import { Dialog } from "primereact/dialog";
import CustomFieldForm from "./CustomFieldForm";
import * as ListCellRenders from "../../../common/list/ListCellRenders";

const CustomSectionDetails = ({
  action,
  createField,
  error,
  fieldUpdateRequest,
  labels,
  menu,
  record,
  requesting,
  sectionDetailsRequest,
  sectionUpdateDetailRequest,
  updateOrder,
  user,
  deleteFieldRequest,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const params = useParams();

  const [isEditable, setIsEditable] = useState(params?.id ? false : true);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedField, setSelectedField] = useState(null);
  const [isCustomFieldDialogVisible, setIsCustomFieldDialogVisible] =
    useState(false);

  const { role } = user || {};
  const canUpdate = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.ContentAccess,
    AccessScopeConstants.UpdateScope
  );

  const canDelete = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.UserAccess,
    AccessScopeConstants.DeleteScope
  );

  useEffect(() => {
    if (params?.id) {
      sectionDetailsRequest({ id: params.id });
    }
  }, [params?.id, sectionDetailsRequest]);

  useEffect(() => {
    if (action === "section-update" && !requesting && !error) {
      navigate(menu?.to);
    } else if (
      (action === "field-create" ||
        action === "field-update-order" ||
        action === "field-update" ||
        action === "field-delete") &&
      !requesting &&
      !error
    ) {
      if (params?.id) {
        setIsCustomFieldDialogVisible(false);
        sectionDetailsRequest({ id: params.id });
      }
    }
  }, [
    error,
    action,
    requesting,
    navigate,
    menu,
    sectionDetailsRequest,
    params.id,
  ]);

  const onUdpdate = (values) => {
    sectionUpdateDetailRequest({
      id: record?.id,
      data: {
        name: values.name,
      },
    });
  };

  const handleFieldAddAndUpdate = (values) => {
    // Find the maximum order value from existing fields and add 1
    const maxOrder =
      record?.fields?.length > 0
        ? Math.max(...record.fields.map((field) => field.order || 0))
        : 0;

    let payload = {
      name: values.name,
      label: values.label,
      type: values.type,
      order: selectedField?.order || maxOrder + 1,
      isRequired: values.isRequired || false,
      showOnRegistration: values.showOnRegistration || false,
    };

    // Handle field type specific options
    if (values.type === 4) {
      // Boolean field - store yes/no labels as options
      payload.options = [values.yes_label, values.no_label];
    } else if (values.type === 3) {
      // Dropdown field - store options array
      payload.options = values.options;
    }

    // Handle default value
    if (values.defaultValue && values.type !== 5) {
      payload.defaultValue = values.defaultValue;
    }

    // Handle TextLong specific fields (Type 6)
    if (values.type === 6) {
      if (values.maxLength) {
        payload.maxLength = parseInt(values.maxLength, 10);
      }
      if (values.placeholder) {
        payload.placeholder = values.placeholder;
      }
    }

    // Handle conditions - convert UI format to API format
    if (values.conditions && values.conditions.length > 0) {
      payload.conditions = values.conditions.map((condition) => ({
        dependentFieldId: condition.dependentFieldId,
        expectedValue: condition.expectedValue,
        // Convert fieldOptions to string - array for dropdown, string for boolean
        optionKey: Array.isArray(condition.fieldOptions)
          ? condition.fieldOptions.join(",") // Convert array to comma-separated string
          : condition.fieldOptions, // Already a string
      }));
    } else {
      payload.conditions = [];
    }

    payload.sectionId = record?.id;

    if (selectedField?.id) {
      fieldUpdateRequest({ id: selectedField.id, data: payload });
    } else {
      createField(payload);
    }
  };

  const handleFieldOrderChange = (field, direction) => {
    const currentFieldIndex = record?.fields.findIndex(
      (item) => item.id === field.id
    );
    const nextFieldIndex =
      direction === "up" ? currentFieldIndex - 1 : currentFieldIndex + 1;
    const currentField = record?.fields[currentFieldIndex];
    const nextField = record?.fields[nextFieldIndex];
    const payload = {
      customFieldIdOne: currentField.id,
      orderOne: nextField.order,
      customFieldIdTwo: nextField.id,
      orderTwo: currentField.order,
    };
    updateOrder({ id: record?.id, data: payload });
  };

  let columns = [
    {
      field: "name",
      header: "field_name",
    },
    {
      field: "type",
      header: "type",
      body: (data) => {
        return intl.formatMessage({
          id: GlobalConstants.FIELD_TYPES.find((item) => item.id === data.type)
            ?.name,
        });
      },
    },
    {
      field: "isRequired",
      header: "required",
      body: (data) => {
        return data.isRequired
          ? intl.formatMessage({ id: "yes" })
          : intl.formatMessage({ id: "no" });
      },
    },
    {
      field: "showOnRegistration",
      header: "show_on_registration",
      body: (data) => {
        return data.showOnRegistration
          ? intl.formatMessage({ id: "yes" })
          : intl.formatMessage({ id: "no" });
      },
    },
  ];

  if (canUpdate && record?.fields?.length > 1) {
    const maxOrder =
      record?.fields?.length > 0
        ? Math.max(...record.fields.map((field) => field.order || 0))
        : 0;
    columns.push({
      field: "order",
      header: "order",
      body: (data) => {
        if (data.order === 1) {
          return (
            <div className="grid gap-2">
              <Button
                type="button"
                icon="pi pi-arrow-down"
                onClick={() => handleFieldOrderChange(data, "down")}
              />
            </div>
          );
        } else if (data.order === maxOrder) {
          return (
            <div className="grid gap-2">
              <Button
                type="button"
                icon="pi pi-arrow-up"
                onClick={() => handleFieldOrderChange(data, "up")}
              />
            </div>
          );
        } else {
          return (
            <div className="grid gap-2">
              <Button
                type="button"
                icon="pi pi-arrow-down"
                onClick={() => handleFieldOrderChange(data, "down")}
              />
              <Button
                type="button"
                icon="pi pi-arrow-up"
                onClick={() => handleFieldOrderChange(data, "up")}
              />
            </div>
          );
        }
      },
    });
  }

  columns.push({
    field: "actions",
    headerIcon: "pi-cog",
    align: "center",
    body: ListCellRenders.OtherActions([
      {
        label: "delete",
        icon: "pi-trash",
        isAllowed: canDelete,
        action: (data) => onDeleteAction(data),
      },
    ]),
  });

  const onDeleteAction = (data) => {
    confirmDialog({
      message: intl.formatMessage(
        { id: "delete_object_message" },
        {
          name: data.name,
          object: intl.formatMessage({ id: "custom_field" }),
        }
      ),
      header: intl.formatMessage({ id: "delete_confirmation" }),
      icon: "pi pi-info-circle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      rejectClassName: "p-button-secondary",
      accept: () => {
        deleteFieldRequest({ id: data.id });
      },
      reject: () => {},
    });
  };

  return (
    <div>
      <CustomBreadCrumbs
        labels={labels}
        navigate={navigate}
        editLabel={record?.name}
      />
      <div className="edexlms-page-content">
        <div className="edexlms-page-header">
          <div className="edexlms-page-title">
            {intl.formatMessage({
              id: "edit_custom_field_section",
            })}
          </div>
          {(params?.id && !isEditable && activeTab === 0 && (
            <div className="edexlms-page-actions">
              <Button
                icon="pi pi-pencil"
                outlined
                onClick={() =>
                  (record?.id && setIsEditable(!isEditable)) || null
                }
                label={intl.formatMessage({ id: "edit" })}
                type="button"
                size="small"
                disabled={!canUpdate || !record?.id}
                tooltipOptions={{ showOnDisabled: true, position: "bottom" }}
                tooltip={
                  canUpdate && !record?.isSystemDefined
                    ? ""
                    : intl.formatMessage({ id: "no_permission" })
                }
              />
            </div>
          )) ||
            null}
          {(params?.id && activeTab === 1 && (
            <div className="edexlms-page-actions">
              <Button
                icon="pi pi-plus"
                outlined
                onClick={() => setIsCustomFieldDialogVisible(true)}
                label={intl.formatMessage({ id: "add_custom_field" })}
                type="button"
                size="small"
                disabled={!canUpdate || !record?.id}
                tooltipOptions={{ showOnDisabled: true, position: "bottom" }}
                tooltip={
                  canUpdate ? "" : intl.formatMessage({ id: "no_permission" })
                }
              />
            </div>
          )) ||
            null}
        </div>
        <div className="edexlms-page-body">
          {(params?.id && (
            <TabMenu
              className="edexlms-tab-menu"
              model={[
                {
                  label: intl.formatMessage({ id: "basic_info" }),
                  command: () => {
                    if (activeTab !== 0) {
                      setActiveTab(0);
                      setIsEditable(false);
                    }
                  },
                },
                {
                  label: intl.formatMessage({ id: "custom_fields" }),
                  command: () => {
                    if (activeTab !== 1) {
                      setActiveTab(1);
                      setIsEditable(false);
                    }
                  },
                },
              ]}
            />
          )) ||
            null}
          {(activeTab === 0 && (
            <Form
              initialValues={{
                name: record?.name,
              }}
              submitBtn={{
                label: intl.formatMessage({
                  id: "update_custom_field_section",
                }),
                onClick: onUdpdate,
              }}
              disabled={!isEditable}
              cancelBtn={{
                label: intl.formatMessage({ id: "cancel" }),
                onClick: () => navigate(menu?.to),
              }}
            >
              <AddCustomFieldSectionForm disabled={!isEditable} />
            </Form>
          )) ||
            null}
          {(activeTab === 1 && (
            <>
              <List
                pageQuery={{
                  sortColumn: "order",
                  sortBy: "asc",
                }}
                handleRowClick={(data) => {
                  setSelectedField(data.data);
                  setIsCustomFieldDialogVisible(true);
                }}
                data={record?.fields || []}
                pagination={false}
                selectionMode=""
                columns={columns}
              />
              <Dialog
                style={{ width: "70vw", minWidth: "500px" }}
                visible={isCustomFieldDialogVisible}
                onHide={() => {
                  setSelectedField(null);
                  setIsCustomFieldDialogVisible(false);
                }}
                header={intl.formatMessage({
                  id: selectedField ? "edit_custom_field" : "add_custom_field",
                })}
              >
                <Form
                  initialValues={{
                    // Always provide TextLong defaults for new fields or existing TextLong fields
                    maxLength: selectedField?.maxLength || 500,
                    placeholder: selectedField?.placeholder || "",
                    type: selectedField?.type || 1,
                    yes_label: selectedField?.options?.[0] || "Yes",
                    no_label: selectedField?.options?.[1] || "No",
                    name: selectedField?.name || "",
                    label: selectedField?.label || "",
                    isRequired: selectedField?.isRequired || false,
                    showOnRegistration:
                      selectedField?.showOnRegistration || false,
                    options: selectedField?.options || [],
                    defaultValue: selectedField?.defaultValue || "",
                    conditions:
                      selectedField?.conditions?.map((condition) => ({
                        dependentFieldId: condition.dependentFieldId,
                        expectedValue: condition.expectedValue,
                        // Convert optionKey string back to appropriate format for UI
                        fieldOptions: (() => {
                          const optionValue =
                            condition.optionKey || condition.fieldOptions;
                          // If main field is dropdown (type 3), convert string to array
                          if (
                            selectedField?.type === 3 &&
                            typeof optionValue === "string"
                          ) {
                            return optionValue
                              .split(",")
                              .filter((item) => item.trim() !== "");
                          }
                          // For boolean or other types, keep as string
                          return optionValue;
                        })(),
                      })) || [],
                  }}
                  submitBtn={{
                    label: intl.formatMessage({
                      id: selectedField
                        ? "update_custom_field"
                        : "add_custom_field",
                    }),
                    onClick: handleFieldAddAndUpdate,
                  }}
                  cancelBtn={{
                    label: intl.formatMessage({ id: "cancel" }),
                    onClick: () => {
                      setSelectedField(null);
                      setIsCustomFieldDialogVisible(false);
                    },
                  }}
                >
                  <CustomFieldForm record={selectedField} />
                </Form>
              </Dialog>
            </>
          )) ||
            null}
        </div>
      </div>
      <ConfirmDialog />
    </div>
  );
};

const mapStateToProps = (state) => ({
  record: state.customFields?.record,
  user: state.self?.user,
  action: state?.customFields?.action,
  error: state?.customFields?.error,
  requesting: state?.customFields?.requesting,
});

const mapDispatchToProps = (dispatch) => ({
  sectionDetailsRequest: (id) => dispatch(sectionDetailsRequest(id)),
  sectionUpdateDetailRequest: (payload) =>
    dispatch(sectionUpdateDetailRequest(payload)),
  createField: (payload) => dispatch(fieldCreateRequest(payload)),
  updateOrder: (payload) => dispatch(fieldUpdateOrderRequest(payload)),
  fieldUpdateRequest: (payload) => dispatch(fieldUpdateRequest(payload)),
  deleteFieldRequest: (payload) => dispatch(deleteFieldRequest(payload)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CustomSectionDetails);
